// Tiny single-slot clipboard for copying attachments between emails.
//
// The browser's own clipboard API can only reliably hold a handful of MIME
// types (text/plain, text/html, image/png). It won't survive a .msg, .xlsx,
// .zip, etc. so we keep a module-scoped copy here. Auto-expires after
// 5 minutes so copies don't linger in memory forever.
//
// Clipboard semantics — single-slot, mutually exclusive with text:
//   - Clicking "Copy" on an attachment chip → set() this slot. We also
//     mark the next ~250 ms as "ours" so the global copy listener
//     doesn't immediately clear what we just set.
//   - User performs any other Copy (Ctrl+C on text, right-click → Copy,
//     etc.) → the global `copy` listener fires and clears the in-app
//     slot, so a subsequent Ctrl+V reaches text-paste and not the
//     stale attachment.
//   - User performs any other paste containing files (osFiles) → that
//     wins, and we clear too.
// This matches the user's expectation that "the file is copied then text
// is copied → the file is overwritten, and vice versa".

const EXPIRY_MS = 5 * 60 * 1000;

let current = null;   // { name, contentType, contentBytes, size, copiedAt }
let timer = null;
let ownCopyUntil = 0; // timestamp; ignore the global copy listener until then
const listeners = new Set();

function notify() { for (const fn of listeners) fn(current); }

function scheduleExpiry() {
  if (timer) clearTimeout(timer);
  timer = setTimeout(() => { current = null; timer = null; notify(); }, EXPIRY_MS);
}

export function set(att) {
  if (!att || !att.contentBytes) return;
  current = {
    name:         att.name || 'attachment',
    contentType:  att.contentType || 'application/octet-stream',
    contentBytes: att.contentBytes,
    size:         att.size || 0,
    copiedAt:     Date.now(),
  };
  // Mark a short grace window so the global copy listener — which sees
  // the system-clipboard write attempted by AttachmentChip.copyFile —
  // doesn't immediately blow away the in-app slot we just set.
  ownCopyUntil = Date.now() + 350;
  scheduleExpiry();
  notify();
}

export function get() {
  return current;
}

export function clear() {
  if (timer) { clearTimeout(timer); timer = null; }
  current = null;
  notify();
}

export function subscribe(fn) {
  listeners.add(fn);
  fn(current);
  return () => listeners.delete(fn);
}

// Wire a global copy listener so any "natural" copy operation (Ctrl+C,
// right-click → Copy, programmatic execCommand from anywhere except
// the AttachmentChip path) replaces the in-app slot — i.e. the most
// recent copy wins, single-item semantics. Idempotent: install once.
// Previously a global `copy` listener wiped this slot whenever the user
// pressed Ctrl+C anywhere (to retain single-slot semantics with the OS
// clipboard). That broke the common workflow "copy attachment → click
// around → paste in another email" because any selection-copy in
// between cleared the slot. The 5-minute auto-expiry already bounds
// staleness; let handlePaste decide which source wins instead.
